/**
 * FitnessProgramCategorySection - Displays a category of fitness program templates
 * Created by neo on 21.01.26
 */
import * as React from 'react';
import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { useTrans } from '../../../Store/System/LocalizationStore';
import { useRootStore } from '../../../Store/useRootStore';
import { useLocalized } from '../../../Hooks/useLocalized';
import { Theme } from '../../../Theme/Theme';
import { FitnessProgramTemplateCategory } from '../../../Model/Coach/Program/Template/FitnessProgramTemplateCategory';
import { FitnessProgramTemplate, FitnessProgramTemplateApi } from '../../../Model/Coach/Program/Template/FitnessProgramTemplate';
import { FitnessProgramTemplateCard } from './FitnessProgramTemplateCard';
import { CoachProgram } from '../../../Model/Coach/Program/CoachProgram';

const Section = styled.div`
  margin-bottom: 48px;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${Theme.Colors.textColor};
  margin: 0;
`;

const ProgramsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 24px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: ${Theme.Colors.charcoal};
  background: ${Theme.Colors.lightestGrey};
  border-radius: 16px;
`;

export type FitnessProgramCategorySectionProps = {
  category: FitnessProgramTemplateCategory;
  activePrograms: CoachProgram[];
};

export const FitnessProgramCategorySection: React.FC<FitnessProgramCategorySectionProps> = observer(
  ({ category, activePrograms }) => {
    const { t } = useTrans();
    const title = useLocalized(category.name);

    const [templates, setTemplates] = useState<FitnessProgramTemplate[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      FitnessProgramTemplateApi.find({ ...category.query, size: 20 })
        .then((res) => setTemplates(res))
        .finally(() => setLoading(false));
    }, [category.query]);

    if (!loading && templates.length === 0) {
      return null;
    }

    return (
      <Section>
        <SectionHeader>
          <div>
            <SectionTitle>{title}</SectionTitle>
          </div>
        </SectionHeader>

        {loading ? (
          <EmptyState>{t('common.loading', { defaultValue: 'Loading...' })}</EmptyState>
        ) : (
          <ProgramsGrid>
            {templates.map((template) => (
              <FitnessProgramTemplateCard
                key={template.id}
                template={template}
                isActive={activePrograms.some((p) => p.superMacroTemplateId === template.id)}
              />
            ))}
          </ProgramsGrid>
        )}
      </Section>
    );
  },
);
